import { styled } from '@mui/material/styles';
import {
  Paper,
  Typography,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Pagination,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { ClientAdmin } from '../../../data/models/client.model';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  selectLoadedAdmins,
  selectLoadingAdmins,
} from '../../../data/client.slice';
import Admin from './Admin';
import { selectLoadingUserRoles } from '../../../data/security.slice';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { PagedList } from '../../../data/models/paged-list.model';

const PREFIX = 'ClientAdmins';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  admins: `${PREFIX}-admins`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    minWidth: 400,
  },

  [`& .${classes.details}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.admins}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface ClientAdminsProps {
  clientAdmins?: PagedList<ClientAdmin>;
  changePage: (page: number) => void;
  addAdmin: () => void;
  resendInvite: (admin: ClientAdmin) => void;
  deleteAdmin: (admin: ClientAdmin) => void;
  readonly: boolean;
}

export default function ClientAdmins({
  clientAdmins,
  changePage,
  addAdmin,
  resendInvite,
  deleteAdmin,
  readonly,
}: ClientAdminsProps) {
  const t = useCommonTranslation();

  // Loading the client admins has a series of requests that are made in sequence
  // so we need to show a loading indicator while any of those requests are being made
  const loadingAdmins = useAppSelector(selectLoadingAdmins);
  const loadingUserRoles = useAppSelector(selectLoadingUserRoles);
  const loadedAdmins = useAppSelector(selectLoadedAdmins);

  const isLoading = loadingAdmins || loadingUserRoles;
  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.admins')}
          </Typography>
          {!readonly && !isSuperReaderUser && (
            <Tooltip title={t('client.addAdmin')}>
              <span>
                <IconButton
                  data-testid="add-admin"
                  onClick={addAdmin}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box className={classes.admins}>
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <CircularProgress />
            </Box>
          )}
          {loadedAdmins &&
            clientAdmins?.items.map((admin, index) => (
              <Admin
                key={admin.userAccountID || `admin-${index}`}
                clientAdmin={admin}
                deleteAdmin={deleteAdmin}
                resendInvite={resendInvite}
                readonly={readonly}
              />
            ))}
          {clientAdmins && clientAdmins.itemCount > 0 && (
            <Pagination
              page={clientAdmins?.currentPage || 0}
              count={clientAdmins?.totalPages}
              onChange={(_evt, page) => changePage(page)}
              color="primary"
            />
          )}
        </Box>
      </Paper>
    </StyledBox>
  );
}
